import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, QrCode, Smartphone, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HomePage: React.FC = () => {
  const appBaseUrl = import.meta.env.VITE_APP_URL || 'http://localhost:3000';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_15%_10%,rgba(19,145,157,0.16),transparent_40%),radial-gradient(circle_at_85%_0%,rgba(245,140,63,0.14),transparent_33%),#f8f5ef] px-4 pb-20 pt-20 sm:px-6">
      <section className="mx-auto max-w-6xl rounded-[2rem] border border-slate-900/10 bg-white/85 p-8 shadow-sm backdrop-blur md:p-12">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">LendLifted</p>
        <h1 className="mt-4 max-w-4xl font-[Sora] text-4xl text-slate-900 md:text-6xl">
          Help more buyers say yes with monthly payment options.
        </h1>
        <p className="mt-5 max-w-3xl text-lg text-slate-700">
          Create a financing quote in seconds, share it by link or QR, and let buyers complete checkout on their own device.
          Built for independent sellers, studios, and service teams.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="rounded-full bg-slate-900 px-6 text-white hover:bg-slate-700">
            <a href={`${appBaseUrl}/auth?mode=signup`}>
              Start selling
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-full border-slate-300 bg-transparent">
            <Link to="/auth">Log in</Link>
          </Button>
        </div>
      </section>

      <section className="mx-auto mt-8 grid max-w-6xl gap-4 md:grid-cols-3">
        <article className="rounded-3xl border border-slate-900/10 bg-white/85 p-6">
          <div className="inline-flex rounded-2xl bg-cyan-100 p-2 text-cyan-800">
            <Smartphone className="h-5 w-5" />
          </div>
          <h2 className="mt-3 font-[Sora] text-xl text-slate-900">Quote from your phone</h2>
          <p className="mt-2 text-sm text-slate-600">Enter amount once and launch financing checkout without switching tools.</p>
        </article>
        <article className="rounded-3xl border border-slate-900/10 bg-white/85 p-6">
          <div className="inline-flex rounded-2xl bg-emerald-100 p-2 text-emerald-800">
            <QrCode className="h-5 w-5" />
          </div>
          <h2 className="mt-3 font-[Sora] text-xl text-slate-900">Share link or QR</h2>
          <p className="mt-2 text-sm text-slate-600">Perfect for in-person sales and quick follow-up by SMS.</p>
        </article>
        <article className="rounded-3xl border border-slate-900/10 bg-white/85 p-6">
          <div className="inline-flex rounded-2xl bg-violet-100 p-2 text-violet-800">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className="mt-3 font-[Sora] text-xl text-slate-900">Track what converts</h2>
          <p className="mt-2 text-sm text-slate-600">See quote views and checkout activity so follow-up happens at the right time.</p>
        </article>
      </section>

      <section className="mx-auto mt-8 max-w-6xl rounded-3xl border border-slate-900/10 bg-white/85 p-6">
        <h3 className="font-[Sora] text-xl text-slate-900">What sellers like most</h3>
        <ul className="mt-4 grid gap-2 text-sm text-slate-700 md:grid-cols-2">
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" /> No ecommerce platform required.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" /> Works for services and high-ticket custom work.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" /> Buyer sees monthly options directly in checkout.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-700" /> Seller workflow stays simple and fast.</li>
        </ul>
      </section>
    </div>
  );
};

export default HomePage;
